import React, { useState } from "react";
import { Button } from "../Button";
import { sanitiseString } from "../../Utils";
import {
  type IconTypes,
  OnsLargeLogo,
  OnsSmallLogo,
  SelectedIcon,
} from "../../Icons";
import { ErrorMarker, ErrorCount } from "../../Styles/Header/ErrorMarkers";
import { Toolbar } from "../../Patterns/Toolbar";

export interface Props {
  text: string;
  description?: string;
  variants?: "internal" | "neutral" | "external";
  signOutNoSave?: boolean;
  signOutSave?: boolean;
  serviceLinks?: ServiceLinks | undefined;
  navigation?: Navigation | undefined;
  toolbar?: ReturnType<typeof Toolbar>;
  handleSignOut?: () => void;
  dataTestId?: string;
}

export interface ItemList {
  text: string;
  iconType?: IconTypes;
  url?: string;
  attributes?: AttributesProps; //?Author Specific attribute
  errorMarker?: boolean;
  errorCount?: number;
}
export interface AttributesProps {
  onClick?: () => void;
}
export interface ServiceLinks {
  id: string;
  ariaLabel?: string;
  ariaListLabel?: string;
  dataTestId?: string;
  itemsList: ItemList[];
  toggleServicesButton: {
    id: string;
    text: string;
    ariaLabel?: string;
    dataTestId: string;
  };
}

export interface Navigation {
  id: string;
  currentPath: string;
  arialLabel?: string;
  dataTestId?: string;
  itemList: ItemList[];
  toggleNavigationButton: {
    id: string;
    text: string;
    ariaLabel?: string;
    dataTestId: string;
  };
}

export const Header = (props: Props) => {
  const [serviceLinksToggleButton, setServiceLinksToggleButton] =
    useState<boolean>(false);
  const [navigationToggleButton, setNavigationToggleButton] =
    useState<boolean>(false);

  return (
    <header
      className={`ons-header ${
        props.variants === "internal"
          ? "ons-header--internal"
          : props.variants === "neutral"
            ? "ons-header--neutral"
            : "ons-header--external"
      }`}
      role="banner"
      data-testid={props.dataTestId}
    >
      <div className="ons-browser-banner">
        <div className="ons-container ons-container--wide">
          <p className="ons-browser-banner__content">
            <span className="ons-browser-banner__lead">
              This website no longer supports your browser.
            </span>
            <span className="ons-browser-banner__cta">
              You can
              <a
                className="ons-browser-banner__link"
                href="https://www.ons.gov.uk/help/browsers"
              >
                upgrade your browser to the latest version
              </a>
              .
            </span>
          </p>
        </div>
      </div>
      <div className="ons-header__top">
        <div className="ons-container ons-container--wide">
          <div className="ons-header__grid-top ons-grid ons-grid--gutterless ons-grid--flex ons-grid--between ons-grid--vertical-center ons-grid--no-wrap">
            <div className="ons-grid__col ons-col-auto">
              <a className="ons-header__org-logo-link" href="#0">
                <div className="ons-header__org-logo ons-header__org-logo--large">
                  {OnsLargeLogo}
                </div>
                <div className="ons-header__org-logo ons-header__org-logo--small">
                  {OnsSmallLogo}
                </div>
              </a>
            </div>
            {props.serviceLinks ? (
              <div className="ons-header__links ons-grid__col ons-col-auto">
                <div className="ons-grid__col ons-col-auto ons-u-d-no@xxs@m">
                  <nav
                    className="ons-header-service-nav ons-header-service-nav--main"
                    aria-label={props.serviceLinks.ariaLabel}
                  >
                    <ul className="ons-header-service-nav__list">
                      {props.serviceLinks?.itemsList.map((item, index) => (
                        <li
                          key={index}
                          className="ons-header-service-nav__item"
                          data-testid={`services-menu-${sanitiseString(
                            item.text
                          )}`}
                        >
                          {item.attributes ? (
                            <a
                              href={item.url ? item.url : "#"}
                              className="ons-header-service-nav__link"
                              onClick={item.attributes?.onClick}
                            >
                              {item.text}
                            </a>
                          ) : (
                            <>
                              {item.iconType && (
                                <SelectedIcon iconType={item.iconType} />
                              )}
                              {item.text}
                            </>
                          )}
                        </li>
                      ))}
                    </ul>
                  </nav>
                </div>
                <div className="ons-grid__col ons-col-auto ons-u-d-no@m">
                  <Button
                    id={props.serviceLinks.toggleServicesButton.id}
                    text={props.serviceLinks.toggleServicesButton.text}
                    classes="ons-btn ons-btn--mobile ons-btn--text-link ons-btn--text-link-inverse"
                    iconType="ArrowIcon"
                    iconPosition="after"
                    onClick={() => {
                      setServiceLinksToggleButton(!serviceLinksToggleButton);
                    }}
                    ariaLabel={
                      props.serviceLinks.toggleServicesButton.ariaLabel
                    }
                    ariaControls="service-links"
                    ariaExpanded={serviceLinksToggleButton}
                    dataTestId={
                      props.serviceLinks.toggleServicesButton.dataTestId
                    }
                  />
                </div>
              </div>
            ) : null}
          </div>
        </div>
        {props.serviceLinks ? (
          <nav
            className={`ons-header-service-nav ons-header-service-nav--mobile ${
              serviceLinksToggleButton ? "ons-u-d-no@l" : "ons-u-d-no" //? Mock DS javascript functionality: https://github.com/ONSdigital/design-system/issues/2770
            } ons-js-services-mobile-nav`}
            id={props.serviceLinks.id}
            aria-label={props.serviceLinks.ariaLabel}
            aria-hidden="true"
          >
            <ul className="ons-header-service-nav__list">
              {props.serviceLinks?.itemsList.map((item, index) => (
                <li
                  key={index}
                  className="ons-header-service-nav__item ons-header-service-nav__item--mobile"
                  data-testid={`services-menu-mobile-${sanitiseString(item.text)}`}
                >
                  {item.url ? (
                    <a
                      href={item.url}
                      className="ons-header-service-nav__link"
                      onClick={item.attributes?.onClick}
                    >
                      {item.text}
                    </a>
                  ) : (
                    <>
                      {item.iconType && (
                        <SelectedIcon iconType={item.iconType} />
                      )}
                      {item.text}
                    </>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        ) : null}
      </div>
      <div className="ons-header__main">
        <div className="ons-container ons-container--wide">
          <div className="ons-grid ons-grid--gutterless ons-grid--flex ons-grid--between ons-grid--vertical-center ons-grid--no-wrap">
            <div className="ons-grid__col ons-col-auto ons-u-flex-shrink">
              <div className="ons-header__title" data-testid="header-title">
                {props.text}
              </div>
              {props.description && (
                <p className="ons-header__description">{props.description}</p>
              )}
            </div>
            {props.signOutSave && !props.signOutNoSave ? (
              <div className="ons-grid__col ons-col-auto ons-u-flex-no-shrink">
                <a
                  href="#0"
                  className="ons-btn ons-u-d-no@xxs@m ons-btn--ghost ons-btn--link ons-js-submit-btn"
                  onClick={props.handleSignOut}
                  data-testid="save-sign-out-button"
                >
                  <span className="ons-btn__inner">
                    <span className="ons-btn__text">Save and Sign Out</span>
                    <SelectedIcon
                      iconType="SignOutLogo"
                      classes="ons-u-ml-xs"
                    />
                  </span>
                </a>
              </div>
            ) : props.signOutNoSave && !props.signOutSave ? (
              <div className="ons-grid__col ons-col-auto ons-u-flex-no-shrink">
                <a
                  href="#0"
                  className="ons-btn ons-u-d-no@xxs@m ons-btn--ghost ons-btn--link ons-js-submit-btn"
                  onClick={props.handleSignOut}
                  data-testid="sign-out-button"
                >
                  <span className="ons-btn__inner">
                    <span className="ons-btn__text">Sign Out</span>
                    <SelectedIcon
                      iconType="SignOutLogo"
                      classes="ons-u-ml-xs"
                    />
                  </span>
                </a>
              </div>
            ) : null}
            {props.navigation ? (
              <div className="ons-grid__col ons-col-auto ons-u-flex-no-shrink">
                <Button
                  id={props.navigation.toggleNavigationButton.id}
                  text={props.navigation.toggleNavigationButton.text}
                  classes="ons-u-ml-xs ons-js-navigation-button ons-u-d-no@l ons-btn--mobile ons-btn--ghost ons-btn--text-link-inverse"
                  iconType="ArrowIcon"
                  iconPosition="after"
                  onClick={() => {
                    setNavigationToggleButton(!navigationToggleButton);
                  }}
                  ariaLabel={props.navigation.toggleNavigationButton.ariaLabel}
                  ariaControls="navigation-links"
                  ariaExpanded={navigationToggleButton}
                  dataTestId={
                    props.navigation.toggleNavigationButton.dataTestId
                  }
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
      {props.navigation ? (
        <div className="ons-navigation-wrapper">
          <div className="ons-container ons-container--wide ons-container--gutterless@xxs@l">
            <nav
              className={`ons-navigation ons-navigation--main ons-js-navigation ${
                navigationToggleButton ? null : "ons-u-d-no@xxs@l"
              }`}
              id={props.navigation.id}
              aria-label={props.navigation.arialLabel}
              data-analytics="header-navigation"
              data-testid={props.navigation.dataTestId}
            >
              <ul className="ons-navigation__list">
                {props.navigation?.itemList &&
                  props.navigation?.itemList.map((item, index) => (
                    <li
                      key={index}
                      className={`ons-navigation__item ${
                        item.url === props.navigation?.currentPath
                          ? "ons-navigation__item--active"
                          : ""
                      }`}
                      data-testid={`main-menu-${sanitiseString(item.text)}`}
                    >
                      <a className="ons-navigation__link" href={item.url}>
                        {item.text}
                        {item.errorCount && item.errorCount > 0 ? (
                          <ErrorCount
                            aria-label="Validation error count"
                            data-testid={`error-count-badge-${sanitiseString(item.text)}`}
                          >
                            <span className="ons-u-vh">
                              Number of validation errors:{" "}
                            </span>
                            {item.errorCount}
                          </ErrorCount>
                        ) : item.errorMarker ? (
                          <ErrorMarker aria-label="Has validation errors">
                            <span className="ons-u-vh">
                              Has validation errors
                            </span>
                          </ErrorMarker>
                        ) : null}
                      </a>
                    </li>
                  ))}
              </ul>
            </nav>
          </div>
        </div>
      ) : null}
      {props.toolbar}
    </header>
  );
};
